import cloneDeep from 'lodash/cloneDeep';
import capitalize from 'lodash/capitalize';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';
import moment from 'moment';

import {openUserSidebar} from 'common/widgets/user-sidebar';
import {extend, getAlphaLabelFromIndex} from 'common/util';
import {getDisplayOptions, mapObjectKeys} from 'common/report/insights/util';
import {
  ChartType,
  DownloadMode,
  PRIMITIVE_MATH_TYPES,
  ProjectFeatureFlag,
  PropertyType,
  ReportName,
  ResourceType,
  SPECIAL_EVENTS,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {FilterOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {BehaviorShowClause, FormulaShowClause} from 'common/report/insights/models/show-clause';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {computeMetricClauseByKey} from 'common/report/insights/util/tooltip';
import {generateTsxRenderer} from 'common/util/tsx-jade-adapter';
import {currentProjectStore} from 'common/report/stores/project-details';
import {findEventDefinitionByDisplayName} from 'common/report/stores/event-definitions';
import {useStore} from 'common/util/component-helpers';
import {EmptyReportHero, NoDataHero} from 'common/widgets/warning-hero/presets';
import {
  getDownloadDisplayPreferences,
  getDownloadMode,
  getEventsReportUrl,
  getProjectId,
  getReportUrl,
  getWorkspaceId,
} from 'common/report/util';
import {getFilterSection, getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getEntries} from 'common/report/insights/util/events';
import {getDateRange, getTimeSection, timestampToXauDateRange} from 'common/report/insights/util/time';
import {createNestedCohort, translateMetricToCohort} from 'common/report/insights/util/translate';
import {
  AnalysisType,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
} from 'common/report/insights/util/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {ConversionWindowUnit, FunnelOrder} from 'common/report/funnels/models/constants';
import {formatDate, getCalendarDate} from 'common/util/date';
import {
  AbsoluteDate,
  DateRangeType,
  OrderedStandardTimeUnits,
  TimeUnit,
} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {MPProperties, NON_NUMERIC_VALUES_SEGMENT, REMAINING_VALUES_SEGMENT} from 'common/constants';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {renameEvent} from 'common/report/util/events';
import {IronContextRegistry} from 'common/report/context-registry';
import 'common/report/insights/charts';
import {Report} from 'common/report/insights/models/report';
import {ProdTrackingEvent} from 'common/report/insights/tracking';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {GroupSection} from 'common/report/insights/models/section';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {pluralize} from 'common/util/string';
import {ColumnResizeEvent, ColumnWidthOverrides, SortChangeEvent} from 'common/components/data-table/types';
import {
  BehaviorDescriptor,
  createCohort as createFunnelCohort,
  getCohortText,
  getCohortTitle,
  getCohortWarning,
} from 'common/report/funnels/visualization/chart-display/utils/cohorts';
import {BarType} from 'common/report/funnels/charts/funnels-step-chart/types';
import {renamePropertyValue} from 'common/report/util/properties';
import {PopoverActionCallback, PopoverCallback, PopoverOption} from 'common/charts/types';
import {CalculationTypes, isMetricEntry, QueryFormulaEntry} from 'common/query-builder/query-entry-section/types';
import {SectionItem} from 'common/components/items-menu/types';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {Clause} from 'common/report/insights/models/clause';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ReportSwitchEvent} from 'common/query-builder/query-builder/types';
import {StepPopoverAction, StepPopoverActionParams} from 'common/report/funnels/visualization/chart-display';
import {hasGroupByDuration, isBehaviorShowClause} from 'common/report/insights/models/utils';
import {BehaviorShowClause as BehaviorShowClauseType} from 'common/report/insights/util/types';
import {PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';
import {
  createDatetimeFilter,
  createFilterFromGroup,
  createNumberFilter,
  getFunnelSegmentNames,
  getNthTimeInPeriodFilter,
  getSelectedDateRange,
  sortFiltersForCohort,
} from 'common/report/insights/visualization/charts/chart-display/utils';
import {SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {CohortType as FunnelCohortType} from 'common/report/funnels/models/types';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {MetricType} from 'common/query-builder/query-block/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {convertSegmentLabelForCohort} from 'common/report/funnels/util';
import {MultimetricBookmarkController} from 'common/report/retention/bookmark-controller/multimetric';
import {CohortType as RetentionCohortType} from 'common/report/retention/models/types';

import {convertSegmentForEventsInspector} from './utils';
import css from './index.inline.styl';
import {template} from './template';

const MultiAttributionDisabledTooltipText = `"Attributed by" breakdown not supported.`;
const CountUsersOnceDisabledTooltipText = `Count Users Once option not supported.`;

const XAU_MATH_TYPE_TIME_UNITS: Map<XAU_MATH_TYPES, TimeUnit> = new Map([
  [XAU_MATH_TYPES.MAU, TimeUnit.Month],
  [XAU_MATH_TYPES.DAU, TimeUnit.Day],
  [XAU_MATH_TYPES.WAU, TimeUnit.Week],
]);

interface ChartDisplayState {
  report: Report;
}

interface ChartDisplayAttrs {
  'active-data-group-id'?: number;
  'resizing-layout'?: boolean;
  'enable-pivot-view'?: boolean;
  'can-use-view-n'?: boolean;
  'can-use-attribution-filter'?: boolean;
  'can-use-attribution-view-users'?: boolean;
  'can-use-attribution-create-cohort'?: boolean;
  'can-use-count-users-once-view-users'?: boolean;
  'can-use-count-users-once-create-cohort'?: boolean;
  'rca-source-id'?: number;
}

interface ChartDisplayProps {
  bookmarkParams: InsightsBookmarkParams;
  queryResults: InsightsQueryResults;
  dataGroups: DataGroup[];
  tableChartColumnWidthOverrides: ColumnWidthOverrides;
}

export type UpdateEvent = CustomEvent<{delta: Partial<InsightsBookmarkParams>}>;

export interface ChartDisplayJsxProps {
  attrs?: ChartDisplayAttrs;
  props: ChartDisplayProps;
  on: {
    update: (ev: UpdateEvent) => void;
    'export-csv': (ev: CustomEvent<void>) => void;
  };
}

export class ChartDisplay extends Component<ChartDisplayState, ChartDisplayAttrs, EmptyObj, void, IronContextRegistry> {
  private _bookmarkParams: InsightsBookmarkParams;
  private _queryResults: InsightsQueryResults;
  private _dataGroups: DataGroup[];
  private _tableChartColumnWidthOverrides: ColumnWidthOverrides = {};

  static get attrsSchema(): AttrsSchema<ChartDisplayAttrs> {
    return {
      ...super.attrsSchema,
      'active-data-group-id': `number`,
      'resizing-layout': `boolean`,
      'enable-pivot-view': `boolean`,
      'can-use-view-n': `boolean`,
      'can-use-attribution-filter': `boolean`,
      'can-use-attribution-view-users': `boolean`,
      'can-use-attribution-create-cohort': `boolean`,
      'can-use-count-users-once-view-users': `boolean`,
      'can-use-count-users-once-create-cohort': `boolean`,
      'rca-source-id': `number`,
    };
  }

  set bookmarkParams(params: InsightsBookmarkParams) {
    this._bookmarkParams = params;
    this.update({report: Report.fromBookmarkData(params)});
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set queryResults(results: InsightsQueryResults) {
    this._queryResults = results;
    this.update();
  }

  get queryResults() {
    return this._queryResults;
  }

  set dataGroups(groups: DataGroup[]) {
    this._dataGroups = groups;
    this.update();
  }

  get dataGroups() {
    return this._dataGroups;
  }

  set tableChartColumnWidthOverrides(overrides: ColumnWidthOverrides) {
    this._tableChartColumnWidthOverrides = overrides;
    this.update();
  }

  get tableChartColumnWidthOverrides() {
    return this._tableChartColumnWidthOverrides;
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`reportLayoutManager` as const, `urlManager` as const, `currentProject` as const, `metrics` as const],
      helpers: {
        extend,
        renderTsx: generateTsxRenderer({
          NoDataHero,
          EmptyReportHero,
        }),
        isDownloadMode: () => getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None,
        enableSegmentsDrillDown: () => {
          const groupSection = this.state.report.sections.group;
          if (!groupSection.hasGroupByPropertyClause()) {
            return groupSection.hasGroupByCohortsClause();
          }
          const groupClauses = this.state.report.sections.group.clauses;
          if (this.hasMultipleListOfObjectsBreakdowns()) {
            return false;
          }
          return this.helpers.checkGroupClauseDrillDownValidity(groupClauses);
        },
        checkGroupClauseDrillDownValidity: (groupClauses: GroupClause[]): boolean => {
          return groupClauses.reduce((accumulator, groupClause) => {
            if (groupClause instanceof GroupSection) {
              return this.helpers.checkGroupClauseDrillDownValidity(groupClause.clauses);
            }
            const groupType = groupClause.typeCast || groupClause.propertyType;
            const isListOfObjects = groupType === `object` && groupClause.joinPropertyType === PropertyType.List;
            const isGroupByCohorts = groupClause.isGroupByCohorts();
            return (
              accumulator &&
              ([`datetime`, `string`, `number`, `boolean`, `list`].includes(groupType) ||
                isListOfObjects ||
                isGroupByCohorts)
            );
          }, true);
        },
        isBehavioralSegmentation: () => {
          const groupClauses = this.state.report.sections.group.clauses;
          return groupClauses.some((clause) => clause.behavior);
        },
        isNoDataProject: () => {
          return !(
            currentProjectStore.get(`hasIntegratedArb`, {defaultValue: true}) ||
            currentProjectStore.get(`hasIntegratedEngage`, {defaultValue: true})
          );
        },
        shouldOpenSidebar: () => currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar),

        getActiveDataGroup: () => {
          const activeDataGroupIdStr = String(this.attr(`active-data-group-id`));
          return this.dataGroups?.find((dg) => dg.groupID === activeDataGroupIdStr);
        },
        getActiveDataGroupDisplayName: () => {
          return this.helpers.getActiveDataGroup()?.displayName;
        },
        getChartLabel: () => {
          let label = `Value`;
          if (this.state.report.displayOptions.value === `relative`) {
            label += ` (Percent of the Group)`;
          }
          return label;
        },
        getDisplayOptions: () => {
          const params = this.state.report.toBookmarkData();
          return getDisplayOptions(params.sections, {
            ...params.displayOptions,
            ...this.helpers.getDownloadDisplayPreferences(),
          });
        },
        getDownloadDisplayPreferences: () => getDownloadDisplayPreferences(this.getContext(`urlManager`)),
        getProjectId,
        getWorkspaceId,
        stringifyObjValues: (obj) => mapObjectKeys(obj, JSON.stringify),
        getTableChartData: () => {
          const metricClauseByKey = computeMetricClauseByKey({
            sectionsMetadata: this.queryResults.meta.report_sections,
            dataGroups: this.dataGroups,
            bookmarkParams: this.state.report.toBookmarkData(),
          });

          return {
            result: this.queryResults,
            meta: this.queryResults.meta,
            headers: this.queryResults.headers,
            metricClauseByKey,
          };
        },
        updateBarColumnWidths: (columnWidths) => {
          this.dispatchUpdate({columnWidths: {...this.state.report.columnWidths, bar: columnWidths}});
        },
        updateBarSortConfig: (barSortConfig) => {
          this.dispatchUpdate({
            sorting: {
              ...this.state.report.sorting,
              bar: barSortConfig,
            },
          });
        },

        getBasePopoverActions: (
          segmentPath: string[],
          {forSegmentColumn = false}: {forSegmentColumn?: boolean} = {},
        ): SectionItem[] => {
          const enableSegmentDrillDown =
            this.helpers.enableSegmentsDrillDown() &&
            !segmentPath.some((segment) => [REMAINING_VALUES_SEGMENT].includes(segment));
          let enableViewEvents = true;
          const clauses = this.getBehaviorClausesFromSegmentPath(segmentPath);
          if (
            clauses.some(
              (clause) => clause.behavior.type === MetricType.Funnel || clause.behavior.type === MetricType.Retention,
            )
          ) {
            enableViewEvents = false;
          }
          let enableViewUsers = true;
          const {chartType} = this.helpers.getDisplayOptions();
          if (
            chartType === ChartType.Metric &&
            clauses.some((clause) => clause.behavior.type === MetricType.Retention)
          ) {
            enableViewUsers = false;
          }
          const viewUsersDisabledReason = this.getViewUsersDisabledReason(segmentPath);
          const viewEventsDisabledReason = this.getViewEventsDisabledReason(segmentPath);
          const filteringDisabledReason = this.getFilteringDisabledReason(segmentPath);

          const dataGroupName = pluralize(this.helpers.getActiveDataGroupDisplayName() || `Users`, true);
          const options = [
            enableSegmentDrillDown && {
              label: `Filter`,
              icon: `filter`,
              value: PopoverOption.Filter,
              ...(filteringDisabledReason && {
                isDisabled: true,
                tooltip: {text: filteringDisabledReason},
              }),
            },
            enableSegmentDrillDown && {
              label: `Exclude`,
              icon: `circle-drop`,
              value: PopoverOption.Exclude,
              ...(filteringDisabledReason && {
                isDisabled: true,
                tooltip: {text: filteringDisabledReason},
              }),
            },
            // view users for metric columns/paths
            enableViewUsers &&
              forSegmentColumn === false && {
                label: `View ${dataGroupName}`,
                icon: `team`,
                value: PopoverOption.ViewUsers,
                ...(viewUsersDisabledReason
                  ? {
                      isDisabled: true,
                      tooltip: {text: viewUsersDisabledReason},
                    }
                  : {}),
              },
            // view events for metric columns/paths
            enableViewEvents &&
              forSegmentColumn === false && {
                label: `View Events`,
                icon: `brand-liveview`,
                value: PopoverOption.ViewEvents,
                ...(viewEventsDisabledReason
                  ? {
                      isDisabled: true,
                      tooltip: {text: viewEventsDisabledReason},
                    }
                  : {}),
              },
          ];

          return options.filter(Boolean);
        },

        lineChartPopoverCallback: (metricKey, segments): SectionItem[] => {
          const {timeUnit} = this.helpers.getDisplayOptions();
          const path = [metricKey, ...segments];
          const disableZoom = this.state.report.isCohortsOverTime() && timeUnit === TimeUnit.Day;
          const options = [
            timeUnit !== TimeUnit.Minute && {
              label: `Zoom In`,
              icon: `search`,
              value: PopoverOption.ZoomIn,
              isDisabled: disableZoom,
            },
            ...this.helpers.getBasePopoverActions(path),
            this.attr(`rca-source-id`) && {
              label: `Root Cause Analysis`,
              icon: `analysis`,
              value: PopoverOption.RCA,
            },
          ];

          return options.filter(Boolean);
        },
        handleLineChartPopoverAction: (ev) => {
          const {pointData, action} = ev.detail;
          const path = [pointData.metricClauseData.metricKey, ...pointData.segments];

          switch (action) {
            case PopoverOption.ViewUsers:
              this.handleLineChartViewUsers(pointData);
              break;
            case PopoverOption.ViewEvents:
              this.handleLineChartViewEvents(pointData);
              break;
            case PopoverOption.ZoomIn:
              this.handleLineChartZoomIn(pointData);
              break;
            case PopoverOption.Filter:
              this.handleTablePopoverFilter(path);
              break;
            case PopoverOption.Exclude:
              this.handleTablePopoverExclude(path);
              break;
            case PopoverOption.RCA:
              this.handleRCAClicked(pointData);
              break;
            default:
              break;
          }
        },

        someSegmentHasMultiAttribution: (segments: string[]): boolean => {
          const clauses = this.getBehaviorClausesFromSegmentPath(segments);
          return clauses ? clauses.some((clause) => clause.measurement.multiAttribution) : false;
        },

        someSegmentHasSegmentMethod: (segmentPath: string[]) => {
          const clauses = this.getBehaviorClausesFromSegmentPath(segmentPath);
          return clauses.some((clause) => clause.measurement.segmentMethod);
        },

        hideCohortFunctionality: (segmentPath: string[]) => {
          return (
            (this.helpers.someSegmentHasMultiAttribution(segmentPath) &&
              !this.attr(`can-use-attribution-create-cohort`)) ||
            (this.helpers.someSegmentHasSegmentMethod(segmentPath) &&
              !this.attr(`can-use-count-users-once-create-cohort`))
          );
        },

        stepsChartPopoverCallback: ({
          action,
          stepIndex,
          segmentProp,
          isDropOff,
          color,
          metricLabel,
        }: StepPopoverActionParams) => {
          if (action === StepPopoverAction.ViewUsers) {
            // in Step Chart, segmentProp is concetenated as a single string
            let segments = segmentProp.split(SEGMENT_SEPARATOR);
            segments = [metricLabel, ...segments];
            this.handleViewUsersForSegment(segments, {
              color,
              isDropOff,
            });
          } else if (action === StepPopoverAction.ViewInFlows) {
            this.handleViewInFlows({stepIndex});
          } else {
            throw new Error(`unsupported action`);
          }
        },

        metricChartPopoverCallback: (segmentName: string, segments: string[]): SectionItem[] => {
          if (segmentName === OTHER_SEGMENT_VALUE) {
            return [];
          }
          return this.helpers
            .getBasePopoverActions(segments)
            .filter((action) => [PopoverOption.ViewUsers, PopoverOption.ViewEvents].includes(action.value));
        },
        shouldDisplayTimeComparison: () => !!this.queryResults.time_comparison,
        dataTableSortChange: (options: SortChangeEvent[`detail`]) => {
          this.dispatchUpdate({
            sorting: {
              ...this.bookmarkParams.sorting,
              [ChartType.Table]: options.sorting,
            },
          });
        },
        dataTableColumnResize: (ev: ColumnResizeEvent) => {
          // we should use `this.dispatchUpdate` instead when columnWidths are added to bookmark for table chart
          propagateEvent(this, ev);
        },
        handleExportCsv: (ev) => {
          propagateEvent(this, ev);
        },
        handleChangeControlGroup: (ev) => {
          this.dispatchUpdate({
            displayOptions: {
              ...this.helpers.getDisplayOptions(),
              statSigControl: ev.detail.props,
            },
          });
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  segmentPopoverCallback: PopoverCallback = (segmentName: string, segmentPath: string[], {forSegmentColumn} = {}) => {
    if (segmentName === OTHER_SEGMENT_VALUE) {
      return [];
    }
    return this.helpers.getBasePopoverActions(segmentPath, {forSegmentColumn});
  };

  createNonRemainingValueFiltersFromCustomGroups(customGroups, groupClause) {
    return customGroups.map((clause) => {
      if (clause.segmentLabel !== REMAINING_VALUES_SEGMENT) {
        return Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });
  }

  createGroupFromCustomGroups(customGroups, groupClause, segmentLabel) {
    let filteredGroupClause;

    customGroups.forEach((clause) => {
      if (clause.segmentLabel === segmentLabel) {
        filteredGroupClause = Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });

    return filteredGroupClause;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getFiltersFromPopoverSegments(segment, {makeNumericRangesExclusive = false} = {}) {
    const filters = [];

    const filterSegments = segment.slice(1, segment.length);
    const groupClauses = this.state.report.sections.group.clauses;
    const metaGroupClauses = [...this.queryResults.meta.report_sections.group];
    const metaCohortsClauses = this.queryResults.meta.report_sections.cohorts || [];
    const timePeriodSettings = this.queryResults.meta.time_period_settings;
    // TODO: Remove this once report_sections contains cohorts in group
    if (metaCohortsClauses.length > 0) {
      const emptyGroupClause = {
        bookmark: {
          value: [],
          resourceType: InsightsResourceType.Events,
        },
      };
      metaGroupClauses.unshift(emptyGroupClause);
    }

    //Build filters for regular breakdowns and value comparisons
    filterSegments.forEach((segmentLabel, idx) => {
      const groupClause: GroupClause = groupClauses[idx];
      const metaGroupClause = metaGroupClauses[idx]?.bookmark;
      const isCustomStringGrouping = Array.isArray(metaGroupClause?.customGroups);
      // for the case of distribution query (histogram), groupClause could be nil
      if (groupClause?.isGroupByCohorts()) {
        const cohortFilter = this.createFilterFromCohort(segmentLabel, true);
        if (cohortFilter) {
          filters.push(cohortFilter);
        }
      } else if (metaGroupClause && Array.isArray(metaGroupClause)) {
        let filteredGroupClause;
        //Only the meta groupClause has the segmentLabel information but we want to use the bookmark groupClause to create the filter
        metaGroupClause.forEach((clause, clauseIndex) => {
          if (clause.segmentLabel === segmentLabel) {
            filteredGroupClause = groupClause[`clauses`][clauseIndex];
          }
        });
        filters.push(
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true, timePeriodSettings),
        );
      } else if (isCustomStringGrouping && segmentLabel === REMAINING_VALUES_SEGMENT) {
        this.createNonRemainingValueFiltersFromCustomGroups(metaGroupClause.customGroups, groupClause).forEach(
          (groupFilter) => {
            filters.push(createFilterFromGroup(groupFilter, groupFilter.filterValue, false, timePeriodSettings));
          },
        );
        // Need to also filter out undefined values so we only have remaining values
        filters.push(createFilterFromGroup(groupClause, `undefined`, false, timePeriodSettings));
      } else if (isCustomStringGrouping && segmentLabel !== `undefined`) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          segmentLabel,
        );
        filters.push(
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true, timePeriodSettings),
        );
      } else if (segmentLabel === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.NotSet,
        });
        filters.push(createFilterFromGroup(numFilter, numFilter.filterValue, true, timePeriodSettings));
      } else if (metaGroupClause) {
        const filter = createFilterFromGroup(groupClause, filterSegments[idx], true, timePeriodSettings);
        if (filter) {
          if (`scdEvent` in groupClause) {
            filter.scdEvent = groupClause.scdEvent;
          }
          // for numeric filters coming from a numerical range breakdown, the segment value is inclusive on the left and exclusive on the right, when
          // makeNumericRangesExclusive is set we transform this into an "at least" and "less than" filter to account for that range definition
          if (
            makeNumericRangesExclusive &&
            filter.filterType === PropertyType.Number &&
            filter.filterOperator === NumberOperator.Between
          ) {
            filters.push(
              {
                ...filter,
                filterOperator: NumberOperator.AtLeast,
                filterValue: filter.filterValue[0],
              },
              {
                ...filter,
                filterOperator: NumberOperator.LessThan,
                filterValue: filter.filterValue[1],
              },
            );
          } else if (filter.filterJoinType === `list`) {
            // check for list filters of the same type, so they can be merged as a single list filter with multiple conditions
            let foundMatch = false;
            filters.forEach((listItem, idx) => {
              if (this.isMatchingBasicListGroup(listItem, filter)) {
                filters[idx].listItemFilters.push(filter.listItemFilters[0]);
                foundMatch = true;
              }
            });
            if (!foundMatch) {
              filters.push(filter);
            }
          } else {
            filters.push(filter);
          }
        }
      }
    });

    return filters;
  }

  isMatchingBasicListGroup(clause1, clause2) {
    const propType1 = clause1.typeCast || clause1.propertyType;
    const propType2 = clause2.typeCast || clause2.propertyType;

    if (
      // check if its a list type
      propType1 !== `object` ||
      clause1.filterJoinType !== `list` ||
      // check they are the same type
      clause1.filterJoinType !== clause2.filterJoinType ||
      clause1.value !== clause2.value ||
      clause1.filterType !== clause2.filterType ||
      propType1 !== propType2
    )
      return false;

    const propValue1 = clause1.listItemFilters[0].value;
    const propValue2 = clause2.listItemFilters[0].value;
    const computedListProps = [`$list_sum`, `$list_unique`, `$list_length`];
    return !(computedListProps.includes(propValue1) || computedListProps.includes(propValue2));
  }

  createFilterFromCohort(filterValue, includesFilterValue) {
    const cohortClauses = this.state.report.sections.group.getGroupByCohortsClauses();
    // The assumption here is no two cohorts in the same project share the same name.
    // It is true in today's production.
    const filteredCohortClauses = cohortClauses.filter(
      (clause) => clause.name === filterValue || `Not In ${clause.name}` === filterValue,
    );

    if (filteredCohortClauses.length > 0) {
      const cohortClause = filteredCohortClauses[0];
      const cohortDataset = cohortClause.dataset;
      const cohortProperties = pick(cohortClause, [`id`, `name`, `negated`, `raw_cohort`]);
      const negated = `Not In ${cohortClause.name}` === filterValue;
      let filterOperator: FilterOperator;
      if (includesFilterValue) {
        filterOperator = negated ? StringOperator.DoesNotContain : StringOperator.Contains;
      } else {
        filterOperator = negated ? StringOperator.Contains : StringOperator.DoesNotContain;
      }
      return {
        filterOperator,
        filterType: `list`,
        filterValue: [
          {
            cohort: {
              ...cohortProperties,
              negated: false,
            },
          },
        ],
        dataset: cohortDataset,
        resourceType: `events`,
        value: `$cohorts`,
      };
    }
    return null;
  }

  track(eventName: ProdTrackingEvent, props?: {[key: string]: any}) {
    emitTrackEvent(this, {eventName, props: props || {}});
  }

  trackSort(group, sortConfig, colIdx?: number) {
    const reportProperties = this.state.report.toTrackingData();
    const eventProperties = {};
    switch (group) {
      case `colSort`:
      case `left`:
        eventProperties[`sort by`] = sortConfig.colSortAttrs[colIdx].sortBy;
        eventProperties[`sort order`] = sortConfig.colSortAttrs[colIdx].sortOrder;
        eventProperties[`sort group`] = sortConfig.sortBy;
        eventProperties[`sort column index`] = colIdx;
        break;
      case `axisSort`:
      case `right`:
        eventProperties[`sort by`] = sortConfig.sortBy;
        eventProperties[`sort order`] = sortConfig.sortOrder;
        eventProperties[`sort group`] = `axis`;
        break;
    }
    this.track(ProdTrackingEvent.ChartSort, extend(reportProperties, eventProperties));
  }

  trackFilter(filter, projectId) {
    this.track(ProdTrackingEvent.PopoverAddFilter, {
      'Property name': filter.value,
      'Property data type': filter.propertyType,
      'Resource type': filter.resourceType,
      'Filter operator': filter.filterOperator,
      'Filter value': filter.filterValue,
      project_id: projectId,
      source: `insights-result`,
    });
  }

  getBehaviorClausesFromSegmentPath(path: string[]): BehaviorShowClause[] | null {
    // eslint-disable-next-line
    const {show: showMetadata} = this.queryResults?.meta.report_sections;
    const {clauses: showClauses} = this.state.report.sections.show;

    if (!showMetadata || !path) {
      return null;
    }

    const findMatchingMetric = (metadata) => metadata.metric_key === path[0];
    const clauseIdx = showMetadata.findIndex(
      (item) => findMatchingMetric(item) || item[`sub_queries`]?.some((item) => findMatchingMetric(item)),
    );

    if (clauseIdx < 0) {
      return null;
    }

    const clause = showClauses[clauseIdx];

    if (clause instanceof FormulaShowClause) {
      const formulaEntry = getResolvedFormulaEntry({
        formulaEntry: {event: clause.formula},
        metricsResource: this.getContext(`metrics`),
      });
      if (!formulaEntry) {
        return null;
      }
      const matchingMetricKeys = new Set(formulaEntry.event.definition.match(/[A-Z]{1,}/g));
      const relevantClauses = formulaEntry.event.referencedMetrics
        ? formulaEntry.event.referencedMetrics.map((clauseAttrs) => new BehaviorShowClause(clauseAttrs))
        : this.state.report.sections.show.clauses;
      const clauses = relevantClauses.filter((_clause, index) => matchingMetricKeys.has(getAlphaLabelFromIndex(index)));

      return clauses.length ? (clauses as BehaviorShowClause[]) : null;
    }

    return clause instanceof BehaviorShowClause ? [clause] : null;
  }

  getViewUsersDisabledReason(path: string[]) {
    const clauses = this.getBehaviorClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    if (clauses.some((clause) => Boolean(clause.measurement.rolling)) && isSubDayTimeUnit) {
      return `${capitalize(timeUnit)} granularity not supported.`;
    }

    if (!Array.isArray(clauses)) {
      return `Clause not found.`;
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts)) {
      return `Cohorts not supported.`;
    }

    if (
      clauses.some(
        (clause) => clause.behavior.resourceType === InsightsResourceType.People && clause.measurement.property,
      )
    ) {
      return `Profile analysis property aggregation not supported.`;
    }

    if (this.helpers.someSegmentHasSegmentMethod(path) && !this.attr(`can-use-count-users-once-view-users`)) {
      return CountUsersOnceDisabledTooltipText;
    }

    if (this.helpers.someSegmentHasMultiAttribution(path) && !this.attr(`can-use-attribution-view-users`)) {
      return MultiAttributionDisabledTooltipText;
    }

    return null;
  }

  getFilteringDisabledReason(path: string[]): string | null {
    if (!this.attr(`can-use-attribution-filter`) && this.helpers.someSegmentHasMultiAttribution(path)) {
      return MultiAttributionDisabledTooltipText;
    }
    return null;
  }

  getViewEventsDisabledReason(path: string[]) {
    const clauses = this.getBehaviorClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    if (clauses.some((clause) => Boolean(clause.measurement.rolling)) && isSubDayTimeUnit) {
      return `${capitalize(timeUnit)} granularity not supported.`;
    }

    if (!Array.isArray(clauses)) {
      return `Clause not found.`;
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts)) {
      return `Cohorts not supported.`;
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.People)) {
      return `Profiles not supported.`;
    }

    if (clauses.some((clause) => (clause.behavior.filters || []).some((filter) => filter.value === `$cohorts`))) {
      return `Inline cohort filters are not supported.`;
    }

    if (this.helpers.someSegmentHasSegmentMethod(path)) {
      return CountUsersOnceDisabledTooltipText;
    }

    if (this.helpers.someSegmentHasMultiAttribution(path)) {
      return MultiAttributionDisabledTooltipText;
    }

    if (this.helpers.isBehavioralSegmentation()) {
      return `Behaviors not supported.`;
    }

    return null;
  }

  addClause(type, clauseData) {
    const report = this.state.report;
    const section = report.sections[type];
    const clause = Clause.create(type, clauseData);
    const sections = report.sections.replaceSection(section.appendClause(clause));
    report.sections = sections;
    this.update({report});
    this.dispatchUpdate({sections: sections.toBookmarkData()});
  }

  handleSegmentPopoverAction: PopoverActionCallback = ({
    segment,
    action,
    isTimeComparisonSegment,
    color,
    columnIndex,
  }) => {
    switch (action) {
      case PopoverOption.Filter:
        this.handleTablePopoverFilter(segment, {columnIndex});
        break;
      case PopoverOption.Exclude:
        this.handleTablePopoverExclude(segment, {columnIndex});
        break;
      case PopoverOption.ViewUsers: {
        this.handleViewUsersForSegment(segment, {
          forTimeComparisonSegment: isTimeComparisonSegment,
          color,
        });
        break;
      }
      case PopoverOption.ViewEvents:
        this.handleViewEventsForSegment(segment);
        break;
    }
  };

  handleTablePopoverFilter(segment: string[], {columnIndex = null}: {columnIndex?: number} = {}) {
    let filters = this.getFiltersFromPopoverSegments(segment);
    // Don't apply filters for segments to the right of where the user clicked.
    if (columnIndex !== null) {
      filters = filters.slice(0, columnIndex + 1);
    }

    filters.forEach((filterIn) => {
      this.addClause(FilterClause.TYPE, filterIn);
      this.trackFilter(filterIn, this.helpers.getProjectId());
    });
  }
  handleRCAClicked(pointData) {
    const clauseData = this.getLineChartPointClauseData(pointData);
    const url = `${getReportUrl(`boards`, {
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
    })}#rca=${this.attr(`rca-source-id`)}&date=${clauseData.dateRange.to}&type=bookmark`;
    window.open(url);
  }

  handleTablePopoverExclude(segment, {columnIndex = null}: {columnIndex?: number} = {}) {
    let targetFilterValue;
    const groupClauses = this.state.report.sections.group.clauses;
    const groupCount = groupClauses.length;

    /** when a columnIndex is set, we want to exclude a specific value in a table, instead of just excluding the leaf segment */
    if (columnIndex !== null) {
      targetFilterValue = segment[columnIndex + 1]; // account for the metric segmentation path
    } else {
      //Bar charts will have "all" as the last segment value so we want to avoid grabbing that and instead grab the value just prior to it
      targetFilterValue =
        segment[segment.length - 1] === `all` ? segment[segment.length - 2] : segment[segment.length - 1];
    }

    const columnTarget = columnIndex ?? groupCount - 1;
    const groupClause = groupClauses[columnTarget];
    const timePeriodSettings = this.queryResults.meta.time_period_settings;
    //We need to use metaGroupClause because it has the segmentLabel attribute that allows us to uniquely identify the relevant groupClause
    // TODO: Fix metaGroupClause TypeError
    const metaGroupClause = this.queryResults.meta.report_sections.group[columnTarget].bookmark;
    const isCustomStringGrouping = Array.isArray(metaGroupClause.customGroups);
    const isCustomNumericGrouping = !!metaGroupClause.customBucket?.groups;

    if (groupCount > 0) {
      if (metaGroupClause && isCustomStringGrouping && targetFilterValue !== `undefined`) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          targetFilterValue,
        );
        this.addClause(
          FilterClause.TYPE,
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, false, timePeriodSettings),
        );
        this.trackFilter(filteredGroupClause, this.helpers.getProjectId());
      } else if (metaGroupClause && isCustomNumericGrouping && targetFilterValue === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.Set,
        });
        this.addClause(
          FilterClause.TYPE,
          createFilterFromGroup(numFilter, numFilter.filterValue, true, timePeriodSettings),
        );
        this.trackFilter(numFilter, this.helpers.getProjectId());
      } else if (metaGroupClause && groupClause.isGroupByCohorts()) {
        const filterOut = this.createFilterFromCohort(targetFilterValue, false);
        if (filterOut) {
          this.addClause(FilterClause.TYPE, filterOut);
        }
      } else {
        const filterOut = createFilterFromGroup(groupClause, targetFilterValue, false, timePeriodSettings);
        if (filterOut) {
          this.addClause(FilterClause.TYPE, filterOut);
          this.trackFilter(filterOut, this.helpers.getProjectId());
        }
      }
    }
  }

  getLineChartPointClauseData(pointData) {
    const timestamp = pointData.isComparisonItem
      ? // it's either time comparison or value comparison
        (pointData.liftComparisonStat || pointData.timeComparisonStat).timestamp
      : pointData.stat.timestamp;
    const clause = pointData.metricClauseData;
    const isSubDayTimeUnit = pointData.timeUnit === TimeUnit.Hour || pointData.timeUnit === TimeUnit.Minute;
    const timeWindowUnit = isSubDayTimeUnit ? TimeUnit.Day : pointData.timeUnit;
    let calculationType;
    if (clause.rolling) {
      calculationType = CalculationTypes.Rolling;
    } else if (clause.cumulative) {
      calculationType = CalculationTypes.Cumulative;
    }
    let dateRangeStr = formatDate(timestamp, {unit: pointData.timeUnit});
    let subDayFilters = [];

    if (XAU_MATH_TYPE_TIME_UNITS.has(clause.math)) {
      const {fromDate, toDate} = timestampToXauDateRange(timestamp, {
        unit: pointData.timeUnit,
        operator: clause.math,
        isLastInterval: pointData.isLastInterval,
        lastIntervalToDate: pointData.stat.dateRange.toDate,
      });

      return {
        calculationType,
        subDayFilters,
        dateRange: new DateRange({
          type: DateRangeType.Between,
          from: getCalendarDate(fromDate ?? toDate),
          to: getCalendarDate(toDate),
        }),
      };
    }

    if (isSubDayTimeUnit) {
      // for hourly line charts, we need to filter on `hour_of_day`
      dateRangeStr = formatDate(timestamp, {unit: TimeUnit.Day});
      subDayFilters = [
        new PropertyFilter({
          filterOperator: StringOperator.Contains,
          // pick out the date, hour & minute component of the date range
          filterValue: moment(timestamp)
            .format()
            .substr(0, pointData.timeUnit === TimeUnit.Minute ? 16 : 13),
          propertyDefaultType: PropertyType.Datetime,
          propertyName: MPProperties.Date,
          propertyType: PropertyType.String,
          resourceType: ResourceType.Event,
        }),
      ];
    }

    const dateFilter = createDatetimeFilter(
      clause.dataset,
      {unit: timeWindowUnit},
      dateRangeStr,
      this.queryResults?.meta.time_period_settings,
      true,
    );

    const dateRange = new DateRange({
      type: DateRangeType.Between,
      ...(Array.isArray(dateFilter.filterValue)
        ? {from: dateFilter.filterValue[0], to: dateFilter.filterValue[1]}
        : {from: dateFilter.filterValue, to: dateFilter.filterValue}),
    });

    return {calculationType, subDayFilters, dateRange, timestamp};
  }

  handleLineChartZoomIn(pointData) {
    const {dateRange, subDayFilters} = this.getLineChartPointClauseData(pointData);
    const selectedTimeUnit = this.state.report.sections.time.clauses[0].unit;
    const selectedFilters = this.state.report.sections.filter;
    const updatedTimeUnit =
      OrderedStandardTimeUnits[OrderedStandardTimeUnits.findIndex((unit) => unit === selectedTimeUnit) - 1];

    const sections = this.state.report.sections
      .replaceSection(getTimeSection(dateRange, {overrideTimeUnit: updatedTimeUnit}))
      .replaceSection(
        getFilterSection(
          [...getPropertyFilters(selectedFilters.clauses), ...subDayFilters],
          getPropertyFiltersOperator(selectedFilters.determiner),
        ),
      )
      .toBookmarkData();

    this.dispatchUpdate({sections});
    this.track(ProdTrackingEvent.PopoverZoomIn, this.state.report.toTrackingData());
  }

  handleLineChartViewUsers(pointData) {
    const {displayOptions} = this.state.report;
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const clauseData = this.getLineChartPointClauseData(pointData);
    const {dateRange, timestamp, calculationType} = clauseData;

    /** cumulative view specifies users up to the point in the chart that was clicked */
    if (displayOptions.analysis === AnalysisType.Cumulative || calculationType === CalculationTypes.Cumulative) {
      dateRange.from = getCalendarDate(this.queryResults.date_range.from_date);
    } else if (displayOptions.analysis === AnalysisType.Rolling || calculationType === CalculationTypes.Rolling) {
      const windowSize = displayOptions.rollingWindowSize;
      dateRange.from = getCalendarDate(
        moment(dateRange.from as AbsoluteDate).subtract(windowSize - 1, pointData.timeUnit),
      );
    }

    this.handleViewUsersForSegment(path, {
      dateRange,
      timestamp,
      subDayFilters: clauseData.subDayFilters,
      forTimeComparisonSegment: pointData.isComparisonItem,
      color: pointData.titleColor,
    });
  }

  getSegmentIndexPropertyName(index: number) {
    const groupBys = this.state.report.sections.group.clauses;
    const group = groupBys[index];

    // for the case of distribution query (histogram), group could be nil
    if (!group || group?.behavior) {
      return null;
    }

    return group.value;
  }

  createFunnelBehaviorDescriptor({
    multiMetricBookmark,
    funnelShowClause,
    groupByValue,
    isDropOff,
  }: {
    multiMetricBookmark: InsightsBookmarkParams;
    funnelShowClause: BehaviorShowClause;
    groupByValue: string[];
    isDropOff: boolean;
  }) {
    // Determine which `step` we are referencing re: View Users.
    // Top of the funnel = number of unique funnel entries over time
    // Bottom of the funnel = number of ultimate funnels over time (All Steps or Last Steps)
    const funnelOnlyMultiMetricBookmark = cloneDeep(multiMetricBookmark);
    funnelOnlyMultiMetricBookmark[`sections`][`show`] = [funnelShowClause];
    const steps = funnelOnlyMultiMetricBookmark[`sections`][`show`][0][`behavior`][`behaviors`];
    // in case of `All Steps` we get `stepIndex = null`
    const stepIndex =
      (funnelOnlyMultiMetricBookmark[`sections`][`show`][0][`measurement`][`stepIndex`]
        ? funnelOnlyMultiMetricBookmark[`sections`][`show`][0][`measurement`][`stepIndex`] + 1
        : steps.length) - 1;

    // If `chartType = line` then Trends, if `chartType = funnel-steps` then Step.
    const chartType = funnelOnlyMultiMetricBookmark[`displayOptions`][`chartType`];

    let behaviorDescriptor = null;
    switch (chartType) {
      case ChartType.Line:
      case ChartType.Bar:
      case ChartType.Metric:
        behaviorDescriptor = {
          index: stepIndex,
          selectedBarType: BarType.CONVERTED,
          groupByValue,
          type: `converted`,
        } as BehaviorDescriptor;
        break;
      case ChartType.FunnelSteps:
        behaviorDescriptor = {
          index: stepIndex,
          selectedBarType: BarType.CONVERTED,
          groupByValue,
          type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : `converted`,
        } as BehaviorDescriptor;
        break;
      default:
        console.error(`Invalid chart type for View Users`);
        return null;
    }

    return behaviorDescriptor;
  }

  handleShowClauseForViewUsersWithPageDuration(showClauses: BehaviorShowClauseType[]): BehaviorShowClause[] {
    // If we have a duration group by, we need to transform events show clauses to funnel based show clauses as
    // this is what query api does for the main query. We cant let query api handle this logic as we want to make sure
    // we want the view users handling for funnels, for e.g. expand should create a place holder cohort in the users page
    // instead of trying to represent duration as a prop in that page which is not supported.
    const transformedShowClauses = [];
    for (const showClause of showClauses) {
      if (isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Event) {
        transformedShowClauses.push(
          new BehaviorShowClause({
            behavior: {
              type: MetricType.Funnel,
              resourceType: InsightsResourceType.Events,
              dataGroupId: showClause.behavior.dataGroupId,
              dataset: showClause.behavior.dataset,
              filters: [],
              conversionWindowDuration: 1,
              conversionWindowUnit: ConversionWindowUnit.Session,
              funnelOrder: FunnelOrder.Loose,
              exclusions: [],
              aggregateBy: [],
              behaviors: [
                {
                  id: showClause.behavior.id,
                  name: showClause.behavior.name,
                  type: showClause.behavior.type,
                  filtersDeterminer: showClause.behavior.filtersDeterminer,
                  funnelOrder: FunnelOrder.Loose,
                },
                {
                  id: showClause.behavior.id,
                  name: showClause.behavior.name,
                  type: showClause.behavior.type,
                  filtersDeterminer: showClause.behavior.filtersDeterminer,
                  funnelOrder: FunnelOrder.Loose,
                },
              ],
            },
            measurement: {
              stepIndex: showClause.measurement.property?.name === PAGE_DURATION.value ? 1 : 0,
              ...showClause.measurement,
            },
          }),
        );
      } else {
        transformedShowClauses.push(showClause);
      }
    }

    return transformedShowClauses;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleViewUsersForSegment(
    segment: string[],
    {
      subDayFilters = [],
      dateRange = null,
      timestamp = null,
      forTimeComparisonSegment = false,
      color = ``,
      isDropOff = false,
    } = {},
  ) {
    let showClauses = this.getBehaviorClausesFromSegmentPath(segment);
    if (showClauses === null) {
      /** @todo: [pablo] Do some error logging here */
      return;
    }
    const isMultiMetricBookmark = showClauses.every((bm) => `measurement` in bm && `behavior` in bm);
    const dataGroup = this.helpers.getActiveDataGroup();
    const dataGroupName = this.helpers.getActiveDataGroupDisplayName();
    const currentProject = this.getCurrentProject();

    if (isMultiMetricBookmark && hasGroupByDuration(this.bookmarkParams.sections.group)) {
      showClauses = this.handleShowClauseForViewUsersWithPageDuration(showClauses);
    }

    let cohortDetail: CohortDetail;
    let segmentLabel: string;
    if (isMultiMetricBookmark && showClauses[0].behavior.type === MetricType.Funnel) {
      const funnelShowClause = showClauses[0];
      const projectAsOfDate = currentProject?.projectAsOfDate;
      const behaviorDescriptor = this.createFunnelBehaviorDescriptor({
        multiMetricBookmark: this.bookmarkParams,
        funnelShowClause,
        groupByValue: segment,
        isDropOff,
      });
      const segmentNames = getFunnelSegmentNames({
        behaviors: funnelShowClause.behavior.behaviors,
        group: this.bookmarkParams.sections.group,
      });
      const selectedDateRange = timestamp
        ? getSelectedDateRange({
            date: timestamp,
            unit: this.state.report.sections.time.clauses[0].unit,
            quarterStartMonth: currentProject.timePeriodSettings?.quarterStartMonth || 0,
          })
        : dateRange;

      const funnelBookmark = {
        ...this.bookmarkParams,
        sections: {
          ...this.bookmarkParams.sections,
          show: [funnelShowClause],
        },
      };
      const cohort = createFunnelCohort({
        name: getCohortTitle({
          index: behaviorDescriptor.index,
          type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
          reportName: `PLACEHOLDER`,
          ...(segmentNames.length > 0
            ? {segment: convertSegmentLabelForCohort(segment.slice(1).join(` / `), segmentNames)}
            : {}),
          dataGroup,
        }),
        description: getCohortText({
          cohortType: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
          stepNames: funnelShowClause.behavior.behaviors.map((beh) => beh.name),
          exclusionSteps: funnelShowClause.behavior.exclusions,
          reportName: `PLACEHOLDER`,
          dateRange: getDateRange(this.state.report.sections.time.clauses),
          conversionRange: null,
          segment:
            segmentNames.length > 0 ? convertSegmentLabelForCohort(segment.slice(1).join(` / `), segmentNames) : null,
          dataGroup,
        }),
        behaviorDescriptor,
        bookmark: null,
        multiMetricBookmark: funnelBookmark,
        query: null,
        selectedDateRange,
      });
      cohortDetail = {
        label: `Create Cohort`,
        type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
        warning: getCohortWarning(
          getDateRange(this.state.report.sections.time.clauses),
          {
            unit: showClauses[0].behavior.conversionWindowUnit,
            value: showClauses[0].behavior.conversionWindowDuration,
          },
          BarType.CONVERTED,
          dataGroup,
          projectAsOfDate,
        ),
        cohort,
      };
      segmentLabel = isDropOff ? `who dropped off` : `who converted`;
    } else if (isMultiMetricBookmark && showClauses[0].behavior.type === MetricType.Retention) {
      const bookmarkController = new MultimetricBookmarkController(this.bookmarkParams, this.queryResults);

      let matchingTrendIndex = 0;
      let matchingTrend = null;
      let absoluteValue = null;
      let index = 0;
      const timeSeries = bookmarkController.getTimeSeries(segment);
      for (const [date, value] of Object.entries(timeSeries)) {
        const formattedDate = formatDate(new Date(date), {iso: true});
        const selectedDate = formatDate(timestamp, {iso: true});
        if (formattedDate === selectedDate) {
          matchingTrendIndex = index;
          matchingTrend = formattedDate;
          absoluteValue = value;
          break;
        }
        index++;
      }
      const retentionBucketIndex = bookmarkController.getRetentionBucketIndex();
      const incompletBuckets = bookmarkController.getIncompletBuckets();
      const completeDate = incompletBuckets[matchingTrend] && incompletBuckets[matchingTrend][retentionBucketIndex];

      const retentionPopover = bookmarkController.createPopover({
        projectTimezone: currentProject?.timezone,
        colIdx: matchingTrendIndex,
        rowIdx: retentionBucketIndex,
        groupedRowIdx: matchingTrendIndex,
        absoluteValue,
        absoluteDropOffValue: null,
        cohortSize: null,
        completeDate,
        enteredValue: bookmarkController.isSegmented() ? segment.slice(1).join(` / `) : formatDate(timestamp),
        enteredDate: new Date(timestamp),
        incomplete: !!completeDate,
        isAggregated: bookmarkController.getSegments().length > 0,
      });
      cohortDetail = retentionPopover.getCohortDetails()[0];
      const type = isDropOff
        ? RetentionCohortType.SpecificIntervalDropOff
        : RetentionCohortType.SpecificIntervalRetained;
      segmentLabel = retentionPopover.getSegmentLabel(type);
    } else {
      const cohort = createNestedCohort(
        showClauses.map((clause, index) =>
          this.getCohortForSegmentShowClause(clause, segment, {
            index,
            subDayFilters,
            dateRange,
            forTimeComparisonSegment,
          }),
        ),
      );
      // TODO: hansli proper name & description for the cohort
      cohort.name = `[Untitled] - ${segment[0]}`;
      cohort.description = `Insight Report: Untitled event: ${segment[0]}`;
      cohortDetail = {
        label: `Create Cohort`,
        type: `insights`,
        warning: ``,
        cohort,
      };
    }

    const segments = [
      segment[0],
      ...segment.slice(1).map((value, index) => renamePropertyValue(value, this.getSegmentIndexPropertyName(index))),
    ];
    const params = {
      include_all_users: !this.state.report.sections.show.isPeopleOnlyQuery(),
      serialized_cohort: encodeURIComponent(JSON.stringify(cohortDetail.cohort.serialize())),
      query_source: `View Users`,
      ...(cohortDetail.cohort.dataGroupId
        ? {
            data_group_id: cohortDetail.cohort.dataGroupId,
          }
        : {}),
    };

    const trackingData = Object.assign(this.state.report.toTrackingData(), {
      'View Users: selected Show Clause': showClauses[0].toBookmarkData(),
      'View Users: selected Segments': segment.slice(1).map((segment, idx) => ({
        segmentValue: segment,
        segmentGroup: this.state.report.sections.group.clauses[idx],
      })),
    });
    this.track(ProdTrackingEvent.PopoverViewUsers, trackingData);

    openUserSidebar(
      {
        'include-all-users': params.include_all_users,
        'report-name': ReportName.Insights,
        'hide-cohort-functionality': this.helpers.hideCohortFunctionality(segment),
        segments,
        ...(segmentLabel ? {'segment-label': segmentLabel} : {}),
        ...(dataGroupName
          ? {
              'data-group-name': dataGroupName,
            }
          : {}),
        ...(dateRange
          ? {
              'from-date': dateRange.from,
              'to-date': dateRange.to,
            }
          : {}),
        color,
      },
      {
        cohort: cohortDetail.cohort,
        cohortDetails: [cohortDetail],
      },
    );
  }

  getCohortForSegmentShowClause(
    showClause,
    segment,
    {index = null, subDayFilters = null, dateRange = null, forTimeComparisonSegment = null} = {},
  ) {
    const entries = getEntries({
      showClauses: [showClause],
      dataGroups: this.dataGroups,
      dateUnit: this.state.report.sections.time.clauses[0].unit,
      reportSectionsMetadata: this.queryResults.meta.report_sections,
    });
    const entry = entries[0] as Exclude<typeof entries[0], QueryFormulaEntry>;
    let frequency = null;

    // for top events, replace the selected top event event value
    const eventValue = isMetricEntry(entry) ? entry.behavior.name : entry.event.value;
    if (eventValue === SPECIAL_EVENTS.TOP_EVENTS) {
      const parsedEventName = segment[0].split(` - `)[0];
      if (isMetricEntry(entry)) {
        entry.behavior.name = findEventDefinitionByDisplayName(parsedEventName)?.name ?? parsedEventName;
        // TODO: MetricEntry needs label field as well
      } else {
        entry.event.value = findEventDefinitionByDisplayName(parsedEventName)?.name ?? parsedEventName;
        entry.event.label = parsedEventName;
      }
    }

    const mathType = isMetricEntry(entry) ? entry.measurement.aggregationOperator : entry.aggregationOperator;
    if (mathType === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
      const lastSegment = segment[segment.length - 1];
      frequency = new FilterClause(createNumberFilter(null, {}, lastSegment, true)).toPropertyFilter();
    }

    const timeComparisonDateRange = this.queryResults.time_comparison?.date_range;
    // handle compare to past
    if (!dateRange && timeComparisonDateRange && forTimeComparisonSegment) {
      const {from_date: from, to_date: to} = timeComparisonDateRange;
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(from),
        to: getCalendarDate(to),
      });
    }

    // bar being clicked, transform to absolute date range
    if (!dateRange && !forTimeComparisonSegment) {
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(this.queryResults.date_range.from_date),
        to: getCalendarDate(this.queryResults.date_range.to_date),
      });
    }

    const nthTimeInPeriodFilter: PropertyFilter = getNthTimeInPeriodFilter(showClause);
    const globalFilterClauses: FilterClause[] = this.state.report.sections.filter.clauses.map((filter) =>
      filter.mapForAttribution(showClause, dateRange),
    );
    // for numerical breakdowns, ranges in the form of A - B, are exclusive on B so we must transform this filter
    // from between to an exclusive on the right filter
    const segmentFilterClauses: FilterClause[] = this.getFiltersFromPopoverSegments(segment, {
      makeNumericRangesExclusive: true,
    }).map((filter) => new FilterClause(filter).mapForAttribution(showClause, dateRange));

    const filters = sortFiltersForCohort(
      subDayFilters as PropertyFilter[],
      nthTimeInPeriodFilter,
      segmentFilterClauses,
      globalFilterClauses,
    );

    return translateMetricToCohort(entry, {
      name: `${getAlphaLabelFromIndex(index)} - ${renameEvent(showClause.behavior.name)}`,
      dateRange,
      filters,
      keepUserAggregation: mathType === PRIMITIVE_MATH_TYPES.HISTOGRAM,
      ...(frequency
        ? {
            frequency,
            frequencyExclusive: true,
          }
        : {}),
    });
  }

  handleLineChartViewEvents(pointData) {
    const {displayOptions} = this.state.report;
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const clauseData = this.getLineChartPointClauseData(pointData);
    const {dateRange, calculationType} = clauseData;

    // TODO: dedupe from view users usage
    /** cumulative view specifies events up to the point in the chart that was clicked */
    if (calculationType === CalculationTypes.Cumulative) {
      dateRange.from = getCalendarDate(this.queryResults.date_range.from_date);
    } else if (calculationType === CalculationTypes.Rolling) {
      const windowSize = displayOptions.rollingWindowSize;
      dateRange.from = getCalendarDate(
        moment(dateRange.from as AbsoluteDate).subtract(windowSize - 1, pointData.timeUnit),
      );
    }

    this.handleViewEventsForSegment(path, {
      dateRange,
      subDayFilters: clauseData.subDayFilters,
    });
  }

  handleViewEventsForSegment(segment: string[], {subDayFilters = null, dateRange = null} = {}) {
    const behaviorShowClauses = this.getBehaviorClausesFromSegmentPath(segment);
    const globalFilters = this.state.report.sections.filter.clauses.map((filterClause) => filterClause.propertyFilter);
    const filtersFromSegments = this.getFiltersFromPopoverSegments(segment).map(
      (filter) => new FilterClause(filter).propertyFilter,
    );
    const timeClause = this.state.report.sections.time.clauses[0];

    this.track(ProdTrackingEvent.PopoverViewEvents, this.state.report.toTrackingData());

    const url = getEventsReportUrl({
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
      entries: convertSegmentForEventsInspector(behaviorShowClauses),
      dateRange: dateRange || getDateRange([timeClause]),
      filters: [].concat(globalFilters, subDayFilters, filtersFromSegments).filter(Boolean),
    });
    if (this.helpers.shouldOpenSidebar()) {
      openEventsSidebar({url});
    } else {
      window.open(url, `_blank`);
    }
  }

  hasMultipleListOfObjectsBreakdowns() {
    const groupClauses: GroupClause[] = this.state.report.sections.group.clauses;

    const numListOfObjects = groupClauses.filter((clause) => !!clause.listItemGroup).length;

    return numListOfObjects > 1;
  }

  handleViewInFlows({stepIndex}: {stepIndex: number}) {
    const detail = {
      report: BookmarkType.Flows,
      newUrlPath: getReportUrl(`flows`, {
        projectId: this.helpers.getProjectId(),
        workspaceId: this.helpers.getWorkspaceId(),
      }),
      additionalContext: {viewAsFlowsContext: {funnelStep: stepIndex - 1}},
    };
    const event: ReportSwitchEvent = new CustomEvent(`reportswitch`, {detail, composed: true, bubbles: true});
    this.track(ProdTrackingEvent.ViewAsFlowClicked);
    this.dispatchEvent(event);
  }

  dispatchUpdate(delta: Partial<InsightsBookmarkParams>) {
    const event: UpdateEvent = new CustomEvent(`update`, {detail: {delta}});
    this.dispatchEvent(event);
  }
}

export const InsightsChartDisplayEl = defineAndMakeJsxFactory<ChartDisplayJsxProps>(
  `insights-chart-display`,
  ChartDisplay,
);
export const InsightsChartDisplayReact = panel2react<ChartDisplayJsxProps>(`insights-chart-display`, ChartDisplay);
